import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Paper, Divider, CircularProgress } from "@mui/material";

// ============================================
// TABLE HELPER COMPONENTS
// ============================================

const Row = ({ label, value }) => (
  <tr style={{ background: "white" }}>
    <td style={{ width: "30%", padding: "6px", border: "1px solid #ddd", fontWeight: 600 }}>
      {label}
    </td>
    <td style={{ width: "70%", padding: "6px", border: "1px solid #ddd", whiteSpace: "pre-wrap" }}>
      {value ?? "—"}
    </td>
  </tr>
);

const Section = ({ title }) => (
  <tr>
    <td colSpan={2} style={{ padding: "8px", border: "1px solid #ddd", fontWeight: "bold", color: "#000" }}>
      {title}
    </td>
  </tr>
);

const CheckRow = ({ checked, label, detail }) => (
  <tr style={{ background: "white" }}>
    <td style={{ width: "30%", padding: "6px", border: "1px solid #ddd", fontWeight: 600 }}>
      <span style={{ marginRight: 8 }}>{checked ? "Checked" : "Unchecked"}</span>
      {label}
    </td>
    <td style={{ width: "70%", padding: "6px", border: "1px solid #ddd", whiteSpace: "pre-wrap" }}>
      {checked ? (String(detail || "").trim() ? detail : "—") : ""}
    </td>
  </tr>
);

// ============================================
// UTILITY FUNCTIONS
// ============================================

const fmtDate = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "—";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const fmtTime = (val) => {
  if (!val) return "—";
  const raw = String(val).trim();

  const ampmAlready = raw.match(/^\s*\d{1,2}(:\d{2})?\s*([AaPp][Mm])\s*$/);
  if (ampmAlready) {
    const [, , ap] = ampmAlready;
    return raw.replace(/([AaPp][Mm])/, ap.toUpperCase()).replace(/\s+/g, " ").trim();
  }

  const m24 = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (m24) {
    let h = parseInt(m24[1], 10);
    const min = m24[2];
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${h}:${min} ${ampm}`;
  }

  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) {
    let h = d.getHours();
    const min = String(d.getMinutes()).padStart(2, "0");
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${h}:${min} ${ampm}`;
  }

  return raw;
};

const firstNonEmpty = (...vals) => {
  for (const v of vals) {
    if (v === 0) return 0;
    if (v !== null && v !== undefined) {
      const s = String(v);
      if (s.trim() !== "") return v;
    }
  }
  return "";
};

const toBool = (v) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v === 1;
  if (typeof v === "string") return ["true", "yes", "y", "1"].includes(v.trim().toLowerCase());
  return !!v;
};

const parseJsonDeep = (input, maxDepth = 5) => {
  let v = input;
  let depth = 0;
  try {
    while (typeof v === "string" && depth < maxDepth) {
      v = JSON.parse(v);
      depth++;
    }
  } catch { }
  return v;
};

const asArray = (val) => {
  const v = parseJsonDeep(val);
  if (Array.isArray(v)) return v;
  if (v && typeof v === "object") return [v];
  return [];
};

// ============================================
// MAIN COMPONENT - POSTPARTUM VISIT
// ============================================

const PostpartumVisitPreview = ({ data }) => {
  const [form, setForm] = useState({});
  const [pp, setPp] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { patientFormId, formTitle, particeName } = data || {};

  const API_BASE_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (!patientFormId) {
      setLoading(false);
      return;
    }

    let active = true;

    const fetchData = async () => {
      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem("neuro_patientportal_auth_token");
        if (!token) throw new Error("Please log in again.");

        const headers = { Authorization: `Bearer ${token}` };

        const formUrl = `${API_BASE_URL}/Forms/GetPatientFormsbyId?Id=${patientFormId}`;
        const ppUrl = `${API_BASE_URL}/DoulaForms/getPostPartumVisitById?PatientFormId=${patientFormId}`;

        const [formRes, ppRes] = await Promise.all([
          fetch(formUrl, { headers }),
          fetch(ppUrl, { headers }),
        ]);

        if (!active) return;

        if (!formRes.ok) throw new Error("Failed to load patient form");

        const formRaw = await formRes.json();
        const formData = Array.isArray(formRaw) ? formRaw[0] || {} : formRaw || {};

        let postpartumData = {};
        if (ppRes.ok) {
          const text = await ppRes.text();
          const parsed = parseJsonDeep(text);
          const arr = asArray(parsed);
          const first = arr[0] || {};
          postpartumData = first.data && typeof first.data === "object" ? first.data : first;
        }

        if (active) {
          setForm(formData);
          setPp(postpartumData);
        }
      } catch (err) {
        if (active) {
          setError(err.message || "Failed to load Postpartum Visit");
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchData();

    return () => {
      active = false;
    };
  }, [API_BASE_URL, patientFormId]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", height: "60vh", alignItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center" sx={{ p: 3 }}>
        {error}
      </Typography>
    );
  }

  // Header & Patient Info
  const practiceName = firstNonEmpty(particeName, form.practiceName);
  const headerTitle = firstNonEmpty(formTitle) || "Postpartum Visit";
  const dosVal = fmtDate(firstNonEmpty(pp.DOS, pp.dos, form.assessmentDate));

  const fileNo = firstNonEmpty(form.accountNo) || "—";
  const clientName = firstNonEmpty(form.patientName, form.clientName) || "—";
  const dobVal = fmtDate(firstNonEmpty(form.dob, form.dateOfBirth));

  // All Postpartum Fields
  const timeIn = firstNonEmpty(pp.timeIn, pp.TimeIn);
  const timeOut = firstNonEmpty(pp.timeOut, pp.TimeOut);
  const location = firstNonEmpty(pp.location, pp.Location);

  const birthDateOfBaby = firstNonEmpty(pp.birthDateOfBaby, pp.BirthDateOfBaby);
  const babyAge = firstNonEmpty(pp.babyAge, pp.BabyAge);

  const newDxMedications = firstNonEmpty(pp.newDxMedicationsDetail, pp.NewDxMedicationsDetail, pp.newDxMedications, pp.NewDxMedications);
  const changeInHousingFinances = toBool(firstNonEmpty(pp.changeInHousingFinances, pp.ChangeInHousingFinances));
  const changeInHousingFinancesDetail = firstNonEmpty(pp.changeInHousingFinancesDetail, pp.ChangeInHousingFinancesDetail);
  const overallHealth = firstNonEmpty(pp.overallHealthDetail, pp.OverallHealthDetail, pp.overallHealth, pp.OverallHealth);

  const nextProviderVisitBirthing = toBool(firstNonEmpty(pp.nextProviderVisitBirthing, pp.NextProviderVisitBirthing));
  const nextProviderVisitBirthingDetail = firstNonEmpty(pp.nextProviderVisitBirthingDetail, pp.NextProviderVisitBirthingDetail);
  const nextProviderVisitBaby = toBool(firstNonEmpty(pp.nextProviderVisitBaby, pp.NextProviderVisitBaby));
  const nextProviderVisitBabyDetail = firstNonEmpty(pp.nextProviderVisitBabyDetail, pp.NextProviderVisitBabyDetail);

  const feedingMethod = firstNonEmpty(pp.feedingMethodDetail, pp.FeedingMethodDetail, pp.feedingMethod, pp.FeedingMethod);
  const lastBabyWeight = firstNonEmpty(pp.lastBabyWeightDetail, pp.LastBabyWeightDetail, pp.lastBabyWeight, pp.LastBabyWeight);
  const answerQuestions = toBool(firstNonEmpty(pp.answerQuestions, pp.AnswerQuestions));
  const answerQuestionsDetail = firstNonEmpty(pp.answerQuestionsDetail, pp.AnswerQuestionsDetail);
  const referralNeeded = toBool(firstNonEmpty(pp.referralNeeded, pp.ReferralNeeded));
  const referralNeededDetail = firstNonEmpty(pp.referralNeededDetail, pp.ReferralNeededDetail);

  const safeSleepReview = toBool(firstNonEmpty(pp.safeSleepReview, pp.SafeSleepReview));
  const safeSleepReviewDetail = firstNonEmpty(pp.safeSleepReviewDetail, pp.SafeSleepReviewDetail);
  const babySleep = firstNonEmpty(pp.babySleepDetail, pp.BabySleepDetail, pp.babySleep, pp.BabySleep);
  const parentsSleep = firstNonEmpty(pp.parentsSleepDetail, pp.ParentsSleepDetail, pp.parentsSleep, pp.ParentsSleep);

  const intentionalRest = toBool(firstNonEmpty(pp.intentionalRest, pp.IntentionalRest));
  const intentionalRestDetail = firstNonEmpty(pp.intentionalRestDetail, pp.IntentionalRestDetail);
  const bleedingLochia = firstNonEmpty(pp.bleedingLochiaDetail, pp.BleedingLochiaDetail, pp.bleedingLochia, pp.BleedingLochia);
  const incontinence = firstNonEmpty(pp.incontinenceDetail, pp.IncontinenceDetail, pp.incontinence, pp.Incontinence);
  const painLevel = firstNonEmpty(pp.painLevelDetail, pp.PainLevelDetail, pp.painLevel, pp.PainLevel);

  const perineumCare = toBool(firstNonEmpty(pp.perineumCare, pp.PerineumCare));
  const perineumCareDetail = firstNonEmpty(pp.perineumCareDetail, pp.PerineumCareDetail);
  const surgicalIncision = toBool(firstNonEmpty(pp.surgicalIncision, pp.SurgicalIncision));
  const surgicalIncisionDetail = firstNonEmpty(pp.surgicalIncisionDetail, pp.SurgicalIncisionDetail);
  const breathExerciseCore = toBool(firstNonEmpty(pp.breathExerciseCore, pp.BreathExerciseCore));
  const breathExerciseCoreDetail = firstNonEmpty(pp.breathExerciseCoreDetail, pp.BreathExerciseCoreDetail);

  const birthReview = toBool(firstNonEmpty(pp.birthReview, pp.BirthReview));
  const birthReviewDetail = firstNonEmpty(pp.birthReviewDetail, pp.BirthReviewDetail);

  const moodPMADs = toBool(firstNonEmpty(pp.moodPMADs, pp.MoodPMADs));
  const moodPMADsDetail = firstNonEmpty(pp.moodPMADsDetail, pp.MoodPMADsDetail);

  const returnToActivity2Weeks = toBool(firstNonEmpty(pp.returnToActivity2Weeks, pp.ReturnToActivity2Weeks));
  const returnToActivity2WeeksDetail = firstNonEmpty(pp.returnToActivity2WeeksDetail, pp.ReturnToActivity2WeeksDetail);
  const returnToActivity6Weeks = toBool(firstNonEmpty(pp.returnToActivity6Weeks, pp.ReturnToActivity6Weeks));
  const returnToActivity6WeeksDetail = firstNonEmpty(pp.returnToActivity6WeeksDetail, pp.ReturnToActivity6WeeksDetail);

  const aapRecommendations = toBool(firstNonEmpty(pp.aapRecommendations, pp.AapRecommendations));
  const aapRecommendationsDetail = firstNonEmpty(pp.aapRecommendationsDetail, pp.AapRecommendationsDetail);
  const babyWearing = toBool(firstNonEmpty(pp.babyWearing, pp.BabyWearing));
  const babyWearingDetail = firstNonEmpty(pp.babyWearingDetail, pp.BabyWearingDetail);

  const referralsMade = firstNonEmpty(pp.referralsMade, pp.ReferralsMade);
  const clientHomework = firstNonEmpty(pp.clientHomework, pp.ClientHomework);
  const doulaHomework = firstNonEmpty(pp.doulaHomework, pp.DoulaHomework);
  const topicsNextVisit = firstNonEmpty(pp.topicsNextVisit, pp.TopicsNextVisit);

  return (
    <Paper sx={{ p: 0, bgcolor: "#fff", borderRadius: 2, boxShadow: "0 3px 8px rgba(0,0,0,0.08)" }}>
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: "-16px",
          zIndex: (t) => t.zIndex.appBar,
          bgcolor: "#fff",
          p: 4,
          pb: 0,
          mb: 3,
        }}
      >
        <Typography variant="subtitle2" sx={{ position: "absolute", top: 16, left: 16 }}>
          <strong>DOS:</strong> {dosVal}
        </Typography>

        <Typography
          variant="h6"
          sx={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            fontWeight: "bold",
            color: "#000",
          }}
        >
          {headerTitle}
        </Typography>

        <Box sx={{ height: 28 }} />
        <Divider />
      </Box>

      {/* Patient Strip */}
      <Box sx={{ px: 4 }}>
        <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Typography variant="body2"><strong>File #:</strong> {fileNo}</Typography>
          </Grid>
          <Grid item xs={4} textAlign="center">
            <Typography variant="body2"><strong>Client Name:</strong> {clientName}</Typography>
          </Grid>
          <Grid item xs={4} textAlign="right">
            <Typography variant="body2"><strong>DOB:</strong> {dobVal}</Typography>
          </Grid>
        </Grid>
        <Divider sx={{ mb: 3 }} />
      </Box>

      {/* Main Content */}
      <Box sx={{ px: 4, pb: 4 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12pt" }}>
          <tbody>
            <Section title="Billing Information" />
            <Row label="Time In" value={fmtTime(timeIn)} />
            <Row label="Time Out" value={fmtTime(timeOut)} />
            <Row label="Location" value={location} />
            <Row label="Birth Date of Baby" value={fmtDate(birthDateOfBaby)} />
            <Row label="Baby's Current Age" value={babyAge} />

            <Section title="Updates" />
            <Row label="New Diagnosis / Medications" value={newDxMedications} />
            <CheckRow checked={changeInHousingFinances} label="Change in housing/finances" detail={changeInHousingFinancesDetail} />
            <Row label="Overall Health" value={overallHealth} />
            <CheckRow checked={nextProviderVisitBirthing} label="Next provider visit (birthing person)" detail={nextProviderVisitBirthingDetail} />
            <CheckRow checked={nextProviderVisitBaby} label="Next provider visit (baby)" detail={nextProviderVisitBabyDetail} />

            <Section title="Feeding the Baby" />
            <Row label="How are you feeding your baby?" value={feedingMethod} />
            <Row label="Last known baby weight" value={lastBabyWeight} />
            <CheckRow checked={answerQuestions} label="Questions answered" detail={answerQuestionsDetail} />
            <CheckRow checked={referralNeeded} label="Referral needed" detail={referralNeededDetail} />

            <Section title="Sleeping" />
            <CheckRow checked={safeSleepReview} label="Safe sleep reviewed" detail={safeSleepReviewDetail} />
            <Row label="Baby sleep (where & frequency)" value={babySleep} />
            <Row label="Parents sleep" value={parentsSleep} />

            <Section title="Healing" />
            <CheckRow checked={intentionalRest} label="Intentional rest & log roll" detail={intentionalRestDetail} />
            <Row label="Bleeding/Lochia" value={bleedingLochia} />
            <Row label="Incontinence (bowel/bladder)" value={incontinence} />
            <Row label="Pain level" value={painLevel} />
            <CheckRow checked={perineumCare} label="Perineum care education" detail={perineumCareDetail} />
            <CheckRow checked={surgicalIncision} label="Surgical incision care" detail={surgicalIncisionDetail} />
            <CheckRow checked={breathExerciseCore} label="Breath & core exercises" detail={breathExerciseCoreDetail} />

            <Section title="Birth Review" />
            <CheckRow checked={birthReview} label="Birth debrief with family" detail={birthReviewDetail} />

            <Section title="Mood / PMADs" />
            <CheckRow checked={moodPMADs} label="Mood & PMAD screening" detail={moodPMADsDetail} />

            <Section title="Return to Activity" />
            <CheckRow checked={returnToActivity2Weeks} label="First 2 weeks postpartum" detail={returnToActivity2WeeksDetail} />
            <CheckRow checked={returnToActivity6Weeks} label="Weeks 2–6 postpartum" detail={returnToActivity6WeeksDetail} />

            <Section title="Baby Movement & Safety" />
            <CheckRow checked={aapRecommendations} label="AAP recommendations reviewed" detail={aapRecommendationsDetail} />
            <CheckRow checked={babyWearing} label="Baby wearing education" detail={babyWearingDetail} />

            <Section title="Referrals Made During Visit" />
            <Row label="Referrals" value={referralsMade} />

            <Section title="Homework and Next Visit" />
            <Row label="Client Homework" value={clientHomework} />
            <Row label="Doula Homework" value={doulaHomework} />
            <Row label="Topics for Next Visit" value={topicsNextVisit} />

            <Section title="Signatures" />
            <Row label="Certified Doula Signature" value={firstNonEmpty(form.messageCertifiedDoulaSignature)} />
            <Row label="Supervisor Signature" value={firstNonEmpty(form.messageSupervisorSignature)} />
            <Row label="RN Signature" value={firstNonEmpty(form.messageRnSignature)} />
            <Row label="NP Signature" value={firstNonEmpty(form.messageNpSignature)} />
            <Row label="OB/GYN Physician Signature" value={firstNonEmpty(form.messageObgynPhysicianSignature)} />
          </tbody>
        </table>
      </Box>
    </Paper>
  );
};

export default PostpartumVisitPreview;